import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { MasterData } from 'src/app/models/masterdata';
import {
    ConfigurationStorageKeys,
    LocalStorageService,
} from 'src/app/services/local-storage.service';
import { MasterdataService } from 'src/app/services/masterdata.service';
import {
    deleteOutdatedHorizon,
    errorDeleteOutdatedHorizon,
    errorFetchAllMasterData,
    errorUpdateOutdatedHorizon,
    fetchAllMasterData,
    successDeleteOutdatedHorizon,
    successFetchAllMasterData,
    successUpdateOutdatedHorizon,
    updateOutdatedHorizon,
} from '../actions/masterdata.action';

@Injectable()
export class MasterDataEffects {
    /**
     * Load master data from local storage and refresh it
     * from the backend.
     */
    public fetchAllMasterData$ = createEffect(() =>
        this._actions$.pipe(
            ofType(fetchAllMasterData),

            switchMap(() =>
                concat(
                    this._localStorageService.fetchLocalStorageValueByKey$(
                        ConfigurationStorageKeys.masterData,
                    ),

                    this._masterdataService
                        .getMasterdata()
                        .pipe(
                            switchMap((masterData: MasterData) =>
                                this._localStorageService.store$(
                                    ConfigurationStorageKeys.masterData,
                                    JSON.stringify(masterData),
                                ),
                            ),
                        ),
                ),
            ),
            map(({ value }) => {
                if (!value) {
                    return null;
                }

                return JSON.parse(value) as MasterData;
            }),
            filter((masterData): masterData is MasterData => !!masterData),

            map((masterData) =>
                successFetchAllMasterData({
                    masterData,
                }),
            ),
            catchError(() => of(errorFetchAllMasterData())),
        ),
    );

    /**
     * Create or update one provider horizon.
     */
    public updateOutdatedHorizon$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateOutdatedHorizon),

            switchMap(({ payload }) =>
                this._masterdataService.updateOutdatedHorizon(payload).pipe(
                    map((outdatedHorizon) =>
                        successUpdateOutdatedHorizon({
                            outdatedHorizon,
                        }),
                    ),

                    catchError((error) =>
                        of(
                            errorUpdateOutdatedHorizon({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Delete one explicit provider horizon.
     */
    public deleteOutdatedHorizon$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteOutdatedHorizon),

            switchMap(({ provider }) =>
                this._masterdataService.deleteOutdatedHorizon(provider).pipe(
                    map((response) =>
                        successDeleteOutdatedHorizon({
                            provider: response.provider,
                        }),
                    ),

                    catchError((error) =>
                        of(
                            errorDeleteOutdatedHorizon({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    constructor(
        private _masterdataService: MasterdataService,
        private _actions$: Actions,
        private _localStorageService: LocalStorageService,
    ) {}
}
